/**
 * Role-Based Access Control (RBAC) Type Definitions
 * 
 * This file defines the core types for the application's RBAC system.
 * Used by both the React app and Node.js scripts.
 * 
 * Database Structure (Current):
 * users/
 *   <userId>/ (document)
 *     roles/ (subcollection)
 *       administrators: { email, assignedBy, assignedAt, role, ... }
 *       practitioners: { email, assignedBy, assignedAt, role, ... }
 *       tutors: { email, assignedBy, assignedAt, role, ... }
 *       clients: { email, assignedBy, assignedAt, role, ... }
 *     uid: string
 *     email: string
 *     roles: string[]  // Array of role names
 *     primaryRole: string  // Primary role
 * 
 * Old Structure (Deprecated - migrated 2026-01-26):
 * user_roles/
 *   administrators/ (document)
 *     users/ (subcollection)
 *       <userId>: { email, assignedBy, assignedAt, ... }
 */

/**
 * User roles in the application
 */
export enum UserRole {
  ADMINISTRATOR = 'administrators',
  PRACTITIONER_ADMINISTRATOR = 'practitionerAdministrators',
  PRACTITIONER = 'practitioners',
  STUDENT = 'students',
  TUTOR = 'tutors',
  SALES = 'sales',
  CLIENT = 'clients',
}

/**
 * Original core roles of the application
 * These are the foundational roles that the system was built with
 */
export const CORE_ROLES = [
  UserRole.ADMINISTRATOR,
  UserRole.PRACTITIONER_ADMINISTRATOR,
  UserRole.PRACTITIONER,
  UserRole.STUDENT,
  UserRole.TUTOR,
  UserRole.SALES,
  UserRole.CLIENT,
] as const;

/**
 * Pre-assignment prefix for users who haven't signed up yet
 * Roles can be pre-assigned to emails before users create accounts.
 * The placeholder ID format is: PRE_ASSIGNMENT_PREFIX + email
 * Example: "pre_user@example.com"
 */
export const PRE_ASSIGNMENT_PREFIX = 'pre_' as const;

/**
 * Type representing the core roles
 */
export type CoreRole = typeof CORE_ROLES[number];

/**
 * Actions that can be performed in the application
 */
export type Action =
  | 'manage' // Special action that represents any action
  | 'read'
  | 'create'
  | 'update'
  | 'delete'
  | 'access' // Used for page/feature access
  | 'switch'; // Used for admin mode switching

/**
 * Subjects (resources) in the application
 */
export type Subject =
  | 'all' // Special subject that represents any subject
  | 'Client'
  | 'Assessment'
  | 'PostureAnalysis'
  | 'Drill'
  /** Globally readable drill-plan definitions (FEATURE_SPEC G3). */
  | 'DrillPlan'
  /** Practitioner-scoped client ↔ drill-plan links (FEATURE_SPEC A1). */
  | 'DrillPlanAssignment'
  | 'Category'
  | 'Settings'
  | 'AdminMode'
  | 'RoleManagement'
  | 'Dashboard'
  | 'Chat'
  | 'Agent' // Harry AI Agent - Practitioners only
  | 'ProgressionModel'
  | 'Coaching'
  | 'Subscription' // Subscription management
  | 'Product' // Product management
  | 'ClientProspect' // Public self-assessment prospects (admin + sales read)
  | 'FunnelAnalytics' // Funnel drop-off metrics (admin + sales read)
  | { __typename: string;[key: string]: any }; // For object-level permissions

/**
 * User role assignment stored in Firebase
 */
export interface UserRoleAssignment {
  userId: string; // Firebase Auth UID
  email: string; // User's email address
  role: UserRole;
  assignedBy: string; // UID of the user who assigned this role
  assignedAt: Date | any; // Date or Firestore Timestamp
  updatedAt: Date | any; // Date or Firestore Timestamp
  isPreAssigned?: boolean; // True if role was assigned before first login
}

/**
 * User information with role for ability definition
 * Supports both single role (backward compatibility) and multiple roles
 */
export interface UserWithRole {
  uid: string;
  email: string | null;
  role: UserRole | UserRole[]; // Can be single role or array of roles
}

/**
 * Role metadata for display and management
 */
export interface RoleMetadata {
  role: UserRole;
  displayName: string;
  description: string;
  /** Semantic text token for role labels (never a raw Tailwind palette class). */
  color: string;
  /** Semantic background token for the role color swatch (legend + grant chips). */
  swatchClass: string;
  icon: string; // Lucide icon name
}

/**
 * Role metadata configuration
 */
export const ROLE_METADATA: Record<UserRole, RoleMetadata> = {
  [UserRole.ADMINISTRATOR]: {
    role: UserRole.ADMINISTRATOR,
    displayName: 'Administrator',
    description: 'Full access to all features including admin mode and system settings',
    color: 'text-error-text',
    swatchClass: 'bg-error',
    icon: 'Shield',
  },
  [UserRole.PRACTITIONER_ADMINISTRATOR]: {
    role: UserRole.PRACTITIONER_ADMINISTRATOR,
    displayName: 'Practitioner Administrator',
    description: 'Practitioner clinical access plus creating and cancelling practitioner-only subscriptions',
    color: 'text-primary',
    swatchClass: 'bg-primary',
    icon: 'UserCog',
  },
  [UserRole.PRACTITIONER]: {
    role: UserRole.PRACTITIONER,
    displayName: 'Practitioner',
    description: 'Full access to client management, assessments, and analysis tools',
    color: 'text-primary',
    swatchClass: 'bg-primary',
    icon: 'User',
  },
  [UserRole.STUDENT]: {
    role: UserRole.STUDENT,
    displayName: 'Student',
    description: 'Full practitioner-like access for education and training purposes',
    color: 'text-warning-text',
    swatchClass: 'bg-warning',
    icon: 'BookOpen',
  },
  [UserRole.TUTOR]: {
    role: UserRole.TUTOR,
    displayName: 'Tutor',
    description: 'Educational access with ability to view and guide students',
    color: 'text-success-text',
    swatchClass: 'bg-success',
    icon: 'GraduationCap',
  },
  [UserRole.SALES]: {
    role: UserRole.SALES,
    displayName: 'Sales',
    description: 'View and search client prospects, funnel metrics, and create new clients',
    color: 'text-primary',
    swatchClass: 'bg-primary',
    icon: 'UserPlus',
  },
  [UserRole.CLIENT]: {
    role: UserRole.CLIENT,
    displayName: 'Client',
    description: 'Limited access to personal assessments and posture analysis',
    color: 'text-primary',
    swatchClass: 'bg-primary',
    icon: 'Users',
  },
};

/**
 * Default role for new users without explicit role assignment
 */
export const DEFAULT_ROLE: UserRole = UserRole.PRACTITIONER;

/**
 * User ban state for admin user management
 */
export type UserBanState = 'banned' | 'unbanned';

/**
 * Ban-related fields stored on user documents
 */
export interface UserBanFields {
  banned: boolean;
  bannedAt?: Date;
  bannedBy?: string;
  bannedReason?: string;
  previousAccessStatus?: string;
}

/**
 * Constants for database structure
 */

export const ROLES_SUBCOLLECTION = 'roles'; // New structure: users/{uid}/roles/{role}

/** CASL Ability type lives in the Access module (#7768). */
export type { CaslAppAbility as AppAbility } from '@/lib/access/casl-types';
